import { gql } from '@apollo/client';
import {
  userDetails,
  candidateProfileDetails,
  hirerProfileDetails,
  hirerCompanyRoles,
} from './sharedApiConstants';

export const LOGIN_REFERENCER = gql`
  mutation signInReferencer($input: SignInReferencerInput!) {
    signInReferencer(input: $input) {
      token
      refreshToken
      referencerProfile {
        referencerProfileId
        user {
          userId
        }
      }
      referenceRequest {
        surveyContainsGrowthCurveQuestions
        referenceRequestId
        company {
          companyId
        }
      }
    }
  }
`;

export const LOGIN_USER = gql`
  mutation signInUser($input: SignInUserInput!) {
    signInUser(input: $input) {
      token
      refreshToken
      permissions
      user {
        userId
        firstName
        username
        primaryEmail
        lastName
        permissions
      }
      tempLogin
    }
  }
`;

export const CREATE_CANDIDATE_USER_ACCOUNT_FOR_POSITION = gql`
  mutation createCandidateAccountAndCreateActiveCPR(
    $input: CreateCandidateAccountAndCreateActiveCPRInput!
  ) {
    createCandidateAccountAndCreateActiveCPR(input: $input) {
      token
      refreshToken
      permissions
      user {
        userId
        firstName
        username
        primaryEmail
        lastName
        permissions
      }
      tempLogin
    }
  }
`;

export const RETURN_USER_FROM_TOKEN = gql`
  query ReturnUserFromToken {
    returnUserFromToken {
      userId
      username
      firstName
      lastName
      primaryEmail
      permissions
      isArchived
      candidateProfile {
        candidateProfileId
        isGamificationCandidate
        isReferenceCandidate
      }
      hirerProfile {
        hirerProfileId
        hirerCompanyRelations {
          hirerCompanyRelationId
          company {
            companyId
            companyKey
            companyName
          }
        }
      }
      referencerProfile {
        referencerProfileId
      }
    }
  }
`;

export const RETURN_CANDIDATE_REFERENCER_USER_FROM_TOKEN = gql`
  query returnUserFromToken {
    returnUserFromToken {
      userId
      firstName
      username
      primaryEmail
      lastName
      permissions
      isArchived
      candidateProfile {
        candidateProfileId
        vanityLinkText
        isPublic
        candidatePhoto
        candidateEmail
        user {
          userId
          firstName
          username
          primaryEmail
          lastName
          permissions
          isArchived
        }
        candidatePositionRelations {
          candidatePositionRelationId
          referencesCompletedDate
          position {
            positionId
            isAssessment360Position
            reviewCycleId
          }
        }
        isPublic
      }
      hirerProfile {
        hirerProfileId
      }
      referencerProfile {
        referencerProfileId
        canAddRecommendation
      }
    }
  }
`;

export const RETURN_HIRER_USER_FROM_TOKEN = gql`
  query returnHirerUserFromToken {
    returnHirerUserFromToken {
      userId
      firstName
      username
      primaryEmail
      lastName
      permissions
      isArchived
      createdAt
      hirerProfile {
        ${hirerProfileDetails}
        ${hirerCompanyRoles} 
        hirerCompanyRelations {
          hirerCompanyRelationId
          permissions
          isAdmin @client
          hirerIsManager

          company {
            companyId
            companyKey
            companyName
            companyLogo
          }
        }
      }
      candidateProfile {
        candidateProfileId
      }
      referencerProfile {
        referencerProfileId
      }
    }
  }
`;

export const REQUEST_USER_RESET = gql`
  mutation RequestUserReset($input: RequestUserResetInput!) {
    requestUserReset(input: $input)
  }
`;

export const UPDATE_USER = gql`
	mutation UpdateUser ($input: UpdateUserInput!) {
		updateUser(input: $input){
			${userDetails}
      permissions
      candidateProfile {
        ${candidateProfileDetails}
      }
      referencerProfile {
        referencerProfileId
      }
      hirerProfile {
				hirerProfileId
				hirerEmail
				isArchived
				user {
					userId
					username
					firstName
					lastName
				}
      }
		}
	}
`;

export const UPDATE_USER_PASSWORD = gql`
	mutation updateUserPassword ($input: UpdateUserPasswordInput!) {
		updateUserPassword(input: $input){
			${userDetails}
    }
	}
`;

export const UPDATED_USER_FRAGMENT = gql`
  fragment updatedUser on User {
    ${userDetails}
    permissions
    candidateProfile {
      ${candidateProfileDetails}
    }
    referencerProfile {
      referencerProfileId
    }
    hirerProfile {
      hirerProfileId
      isArchived
    }
  }
`;

export const TAKEOVER_USER = gql`
  mutation TakeoverUserInput($input: TakeoverUserInput!) {
    takeoverUser(input: $input) {
      token
      refreshToken
      permissions
      adminUserId
      adminUserEmail
      tempLogin
      user {
        userId
        firstName
        username
        primaryEmail
        lastName
        permissions
      }
    }
  }
`;

export const GET_GOOGLE_LOGIN_URL = gql`
  query googleLoginUrlForUserType($input: GoogleLoginUrlInput!) {
    googleLoginUrlForUserType(input: $input)
  }
`;

export const LOGIN_BY_GOOGLE = gql`
  mutation signInWithGoogle($input: SignInWithGoogleInput!) {
    signInWithGoogle(input: $input) {
      token
      refreshToken
      permissions
      tempLogin
      user {
        userId
        firstName
        username
        primaryEmail
        lastName
        permissions
        isArchived
        hirerProfile {
          hirerProfileId
          hirerEmail
          isArchived
        }
        referencerProfile {
          referencerProfileId
          referencerEmail
          isArchived
          canAddRecommendation
        }
        candidateProfile {
          candidateProfileId
          isArchived
        }
      }
    }
  }
`;

export const UPDATE_MY_USER_SETTINGS = gql`
  mutation updateMyUserSettings($input: UpdateMyUserSettingsInput!) {
    updateMyUserSettings(input: $input) {
      ${userDetails}
    }
  }
`;

export const CREATE_RECOMMENDER_FROM_CANDIDATE = gql`
  mutation createRecommenderFromCandidate {
    createRecommenderFromCandidate {
      userId
      primaryEmail
      firstName
      lastName
      isArchived
      username
      referencerProfile {
        referencerProfileId
        linkedinUrl
        referencerEmail
        isArchived
        canAddRecommendation
      }
      candidateProfile {
        candidateProfileId
        candidateEmail
        isGamificationCandidate
      }
    }
  }
`;

export const CREATE_CANDIDATE_FROM_REFERENCER = gql`
  mutation createGamificationCandidateFromReferencer {
    createGamificationCandidateFromReferencer {
      userId
      primaryEmail
      firstName
      lastName
      isArchived
      username
      referencerProfile {
        referencerProfileId
        linkedinUrl
        referencerEmail
        isArchived
      }
      candidateProfile {
        candidateProfileId
        isGamificationCandidate
        candidateEmail
        candidatePositionRelations {
          candidatePositionRelationId
          position {
            positionId
            isCandidateInitiatedPosition
          }
        }
      }
    }
  }
`;

export const SIGN_IN_WITH_WORKOS = gql`
  mutation signInWithWorkos($input: SignInWithWorkosInput!) {
    signInWithWorkos(input: $input) {
      token
      refreshToken
      permissions
      user {
        userId
        firstName
        username
        primaryEmail
        lastName
        permissions
        hirerProfile {
          hirerProfileId
          hirerEmail
          isArchived
        }
        referencerProfile {
          referencerProfileId
          referencerEmail
          isArchived
          canAddRecommendation
        }
        candidateProfile {
          candidateProfileId
          isArchived
        }
      }
      tempLogin
    }
  }
`;

export const GET_WORK_OS_LOGIN_URL = gql`
  mutation AuthorizeWorkos($input: AuthorizeWorkosInput!) {
    authorizeWorkos(input: $input) {
      authorizationURL
    }
  }
`;

export const SIGN_IN_HIRER_WITH_GOOGLE = gql`
  mutation signInHirerWithGoogle($input: SignInHirerWithGoogleInput!) {
    signInHirerWithGoogle(input: $input) {
      token
      refreshToken
      permissions
      tempLogin
      user {
        userId
        firstName
        username
        primaryEmail
        lastName
        permissions

        hirerProfile {
          hirerProfileId
        }
      }
    }
  }
`;

export const SIGN_IN_HIRER_USER = gql`
  mutation signInHirerUser($input: SignInHirerUserInput!) {
    signInHirerUser(input: $input) {
      token
      refreshToken
      permissions
      tempLogin
      user {
        userId
        firstName
        username
        primaryEmail
        lastName
        permissions
        hirerProfile {
          ${hirerProfileDetails}
        }
      }
    }
  }
`;

export const LOGOUT_USER = gql`
  mutation logoutUser {
    logoutUser {
      success
    }
  }
`;
